import { calculateAnthropometry, resolvePatientAge } from "./antropometria.js";

export const objectiveRules = {
  perdida_grasa: {
    label: "Perdida de grasa",
    calorieAdjustment: 0,
    proteinGkg: 1.8,
    carbGkg: 3,
    fatGkg: 0.8,
    fatPercent: 25,
  },
  aumento_masa: {
    label: "Aumento de masa muscular",
    calorieAdjustment: 0,
    proteinGkg: 1.8,
    carbGkg: 5,
    fatGkg: 1,
    fatPercent: 25,
  },
  mantenimiento: {
    label: "Mantenimiento",
    calorieAdjustment: 0,
    proteinGkg: 1.6,
    carbGkg: 4,
    fatGkg: 0.9,
    fatPercent: 30,
  },
};

export const nafCatalog = {
  ligera: {
    label: "Ligera",
    range: "1.40-1.69",
    male: 1.55,
    female: 1.56,
    description: "Actividad diaria ligera segun rangos RIEN/FAO PAL.",
  },
  moderada: {
    label: "Moderada",
    range: "1.70-1.99",
    male: 1.78,
    female: 1.64,
    description: "Actividad moderada segun rangos RIEN/FAO PAL.",
  },
  fuerte: {
    label: "Fuerte",
    range: "2.00-2.40",
    male: 2.1,
    female: 1.82,
    description: "Actividad alta/fuerte segun rangos RIEN/FAO PAL.",
  },
};

export const macroEnergyAdequacyRange = {
  min: 95,
  max: 105,
};

export const DEFAULT_ADDITION_DAYS = 30;

export const activityToNafCategory = {
  bajo: "ligera",
  medio: "moderada",
  alto: "fuerte",
};

export const activityFactors = {
  bajo: nafCatalog.ligera.female,
  medio: nafCatalog.moderada.male,
  alto: nafCatalog.fuerte.male,
};

export function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundNumber(value, decimals = 0) {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function positiveNumber(value) {
  const parsed = toNumber(value);
  return parsed > 0 ? parsed : 0;
}

function hasEnteredValue(value) {
  return value !== "" && value !== null && value !== undefined;
}

function parseValidationDate(value) {
  if (!hasEnteredValue(value)) return null;
  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) return null;
  return parsed;
}

function currentBogotaDate() {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Bogota",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date()).map((part) => [part.type, part.value]),
  );
  return parseValidationDate(`${parts.year}-${parts.month}-${parts.day}`);
}

function addNumericValidationError(errors, patient, field, label, {
  minimum = 0,
  exclusiveMinimum = false,
  maximum = null,
  exclusiveMaximum = false,
  integer = false,
} = {}) {
  const raw = patient?.[field];
  if (!hasEnteredValue(raw)) return;
  const value = Number(raw);
  const belowMinimum = exclusiveMinimum ? value <= minimum : value < minimum;
  const aboveMaximum = maximum === null
    ? false
    : exclusiveMaximum ? value >= maximum : value > maximum;
  if (!Number.isFinite(value) || belowMinimum || aboveMaximum || (integer && !Number.isInteger(value))) {
    const range = maximum === null
      ? `${exclusiveMinimum ? "mayor que" : "igual o mayor que"} ${minimum}`
      : `entre ${exclusiveMinimum ? "más de " : ""}${minimum} y ${exclusiveMaximum ? "menos de " : ""}${maximum}`;
    errors.push(`${label} debe ser un número ${range}${integer ? " y entero" : ""}.`);
  }
}

function patientSexKey(patient) {
  if (patient?.sexo === "masculino") return "male";
  if (patient?.sexo === "femenino") return "female";
  return "";
}

export function getNafCategory(patient) {
  const explicit = String(patient?.naf_categoria ?? "").trim().toLowerCase();
  if (nafCatalog[explicit]) return explicit;
  return activityToNafCategory[patient?.actividad] ?? "moderada";
}

export function getSuggestedNaf(patient) {
  const category = getNafCategory(patient);
  const record = nafCatalog[category] ?? nafCatalog.moderada;
  const sexKey = patientSexKey(patient);
  return sexKey ? record[sexKey] : 0;
}

export function getNafValue(patient) {
  const manual = positiveNumber(patient?.naf_valor);
  const category = getNafCategory(patient);
  const record = nafCatalog[category] ?? nafCatalog.moderada;
  return {
    category,
    suggested: getSuggestedNaf(patient),
    value: manual || getSuggestedNaf(patient),
    isManual: manual > 0,
    range: record.range,
    description: record.description,
  };
}

function estimateLeanMass(patient) {
  const explicitLeanMass = positiveNumber(patient?.masa_libre_grasa_kg);
  if (explicitLeanMass) {
    return { value: explicitLeanMass, source: "manual" };
  }
  const anthropometry = calculateAnthropometry(patient);
  const calculatedLeanMass = anthropometry.derivedBasics?.leanMass;
  if (calculatedLeanMass?.value) {
    return {
      value: calculatedLeanMass.value,
      source: `antropometria_${calculatedLeanMass.source}`,
    };
  }
  if (anthropometry.primaryComposition?.leanKg) {
    return {
      value: anthropometry.primaryComposition.leanKg,
      source: `antropometria_${anthropometry.primaryComposition.method}`,
    };
  }
  return { value: 0, source: "faltante" };
}

function getIdealWeight(patient) {
  const explicitIdeal = positiveNumber(patient?.peso_ideal_kg);
  if (explicitIdeal) return { value: explicitIdeal, source: "peso_ideal_kg" };
  const objectiveWeight = positiveNumber(patient?.peso_objetivo_kg);
  if (objectiveWeight) return { value: objectiveWeight, source: "peso_objetivo_kg" };
  const anthropometry = calculateAnthropometry(patient);
  if (anthropometry.derivedBasics?.idealWeightKg) {
    return { value: anthropometry.derivedBasics.idealWeightKg, source: anthropometry.derivedBasics.idealWeightSource };
  }
  const currentWeight = positiveNumber(patient?.peso_kg);
  if (currentWeight) return { value: currentWeight, source: "peso_kg" };
  return { value: 0, source: "faltante" };
}

export function calculateCunninghamBmr(patient) {
  const leanMass = estimateLeanMass(patient);
  if (!leanMass.value) {
    return {
      value: 0,
      leanMassKg: 0,
      leanMassSource: leanMass.source,
      formula: "370 + 21.6 * masa_libre_grasa_kg",
    };
  }
  return {
    value: roundNumber(370 + 21.6 * leanMass.value, 3),
    leanMassKg: leanMass.value,
    leanMassSource: leanMass.source,
    formula: "370 + 21.6 * masa_libre_grasa_kg",
  };
}

export function calculateHarrisBenedictBmr(patient) {
  const idealWeight = getIdealWeight(patient);
  const height = positiveNumber(patient?.talla_cm);
  const age = resolvePatientAge(patient);
  const sexKey = patientSexKey(patient);
  const formula = sexKey === "male"
    ? "65.5 + 13.75 * peso_ideal_kg + 5.003 * talla_cm - 6.775 * edad"
    : sexKey === "female"
      ? "655.1 + 9.563 * peso_ideal_kg + 1.85 * talla_cm - 4.676 * edad"
      : "Requiere sexo para seleccionar la ecuacion Harris-Benedict";
  if (!sexKey || !idealWeight.value || !height || !age) {
    return {
      value: 0,
      idealWeightKg: idealWeight.value,
      idealWeightSource: idealWeight.source,
      formula,
    };
  }
  const value = sexKey === "male"
    ? 65.5 + 13.75 * idealWeight.value + 5.003 * height - 6.775 * age
    : 655.1 + 9.563 * idealWeight.value + 1.85 * height - 4.676 * age;
  return {
    value: roundNumber(value, 3),
    idealWeightKg: idealWeight.value,
    idealWeightSource: idealWeight.source,
    formula,
  };
}

export function calculateBmr(patient) {
  const harris = calculateHarrisBenedictBmr(patient);
  if (harris.value) return harris.value;
  return calculateCunninghamBmr(patient).value;
}

export function calculateGetAdjustments(patient) {
  const anthropometry = calculateAnthropometry(patient);
  const selectedTarget = anthropometry.targets?.selected ?? {};
  const hasManualAdiposeTarget = hasEnteredValue(patient?.kg_masa_adiposa_perder);
  const hasManualMuscleTarget = hasEnteredValue(patient?.kg_masa_muscular_ganar);
  const adiposeKg = hasManualAdiposeTarget
    ? positiveNumber(patient?.kg_masa_adiposa_perder)
    : positiveNumber(selectedTarget.kgAdiposeToLose);
  const restrictionDays = positiveNumber(patient?.dias_restriccion);
  const muscleKg = hasManualMuscleTarget
    ? positiveNumber(patient?.kg_masa_muscular_ganar)
    : positiveNumber(selectedTarget.kgMuscleToGain);
  const additionDays = muscleKg > 0
    ? positiveNumber(patient?.dias_adicion) || DEFAULT_ADDITION_DAYS
    : 0;

  const restrictionError = adiposeKg > 0 && restrictionDays <= 0
    ? "La restricción tiene kg de masa adiposa, pero requiere días de restricción mayores que cero."
    : "";
  const additionError = "";

  const deficitTotalKcal = adiposeKg ? adiposeKg * (3500 / 0.454) : 0;
  const deficitDailyKcal = deficitTotalKcal && restrictionDays ? deficitTotalKcal / restrictionDays : 0;
  const adiposeLossGWeek = deficitDailyKcal ? ((deficitDailyKcal * 7) / 3500) * 454 : 0;

  const additionTotalKcal = muscleKg ? 7000 * muscleKg : 0;
  const additionDailyKcal = additionTotalKcal && additionDays ? additionTotalKcal / additionDays : 0;
  const muscleGainGWeek = additionDailyKcal ? ((additionDailyKcal * 7) / 7000) * 500 : 0;

  return {
    restriction: {
      kgAdiposeToLose: adiposeKg,
      days: restrictionDays,
      valid: !restrictionError,
      error: restrictionError,
      totalKcal: roundNumber(deficitTotalKcal, 2),
      dailyKcal: roundNumber(deficitDailyKcal, 2),
      gramsPerWeek: roundNumber(adiposeLossGWeek, 1),
      source: hasManualAdiposeTarget
        ? "manual"
        : adiposeKg && selectedTarget.method
          ? `metas_${selectedTarget.method}`
          : "sin_ajuste",
      formula: "(kg_masa_adiposa_perder * 3500 / 0.454) / dias_restriccion",
    },
    addition: {
      kgMuscleToGain: muscleKg,
      days: additionDays,
      valid: !additionError,
      error: additionError,
      totalKcal: roundNumber(additionTotalKcal, 2),
      dailyKcal: roundNumber(additionDailyKcal, 2),
      gramsPerWeek: roundNumber(muscleGainGWeek, 1),
      source: hasManualMuscleTarget
        ? "manual"
        : muscleKg && selectedTarget.method
          ? `metas_${selectedTarget.method}`
          : "sin_ajuste",
      formula: "(7000 * kg_masa_muscular_ganar) / dias_adicion",
      daysSource: muscleKg > 0 && positiveNumber(patient?.dias_adicion) ? "legacy" : "estandar_excel_30_dias",
    },
    valid: !restrictionError && !additionError,
    errors: [restrictionError, additionError].filter(Boolean),
  };
}

export function calculateEnergy(patient) {
  const cunningham = calculateCunninghamBmr(patient);
  const harrisBenedict = calculateHarrisBenedictBmr(patient);
  const naf = getNafValue(patient);
  const adjustments = calculateGetAdjustments(patient);

  const get1Cunningham = cunningham.value ? roundNumber(cunningham.value * naf.value, 2) : 0;
  const get1Harris = harrisBenedict.value ? roundNumber(harrisBenedict.value * naf.value, 2) : 0;
  const adjustmentKcal = adjustments.addition.dailyKcal - adjustments.restriction.dailyKcal;
  const get2CunninghamLiteral = adjustments.valid && get1Cunningham ? roundNumber(get1Cunningham + adjustmentKcal, 2) : 0;
  const get2HarrisLiteral = adjustments.valid && get1Harris ? roundNumber(get1Harris + adjustmentKcal, 2) : 0;
  const isPediatric = patient?.population_profile === "pediatric";
  const calculatedGet2 = [get2HarrisLiteral, get2CunninghamLiteral]
    .find((value) => value > 0) || 0;
  const get2Suggested = isPediatric ? 0 : calculatedGet2;
  const manualGet3 = positiveNumber(patient?.get_3);
  const finalEnergyRaw = manualGet3 || get2Suggested;
  const target = roundNumber(finalEnergyRaw);
  const bmr = harrisBenedict.value || cunningham.value || 0;
  const total = get1Harris || get1Cunningham || 0;
  const training = roundNumber(toNumber(patient?.entrenamientos_semana) * 90);
  const literalNotes = [
    "GET2 se calcula segun el Excel actualizado: GET1 - restriccion kcal/dia + adicion kcal/dia.",
  ];

  if (adjustments.errors.length) literalNotes.push(...adjustments.errors);
  if (isPediatric && !manualGet3) {
    literalNotes.push("En población pediátrica, Cunningham y Harris-Benedict se conservan solo como referencia trazable; se requiere un GET3 definido por la profesional.");
  } else if (!manualGet3) {
    literalNotes.push("GET3 aun no ha sido confirmado por la profesional; se muestra GET2 como sugerencia y no se modifica con un piso oculto.");
  }

  if (String(cunningham.leanMassSource).startsWith("antropometria_")) {
    literalNotes.push("Masa libre de grasa tomada de la valoración antropométrica avanzada porque no se diligenció una corrección profesional manual.");
  }
  if (harrisBenedict.idealWeightSource && !["peso_ideal_kg", "faltante"].includes(harrisBenedict.idealWeightSource)) {
    literalNotes.push(`Harris-Benedict usa ${harrisBenedict.idealWeightSource} como respaldo porque peso_ideal_kg esta vacio.`);
  }

  return {
    bmr,
    activityFactor: naf.value,
    occupationalAndExercise: roundNumber(Math.max(total - bmr, 0), 2),
    trainingEstimate: training,
    total,
    adjustment: roundNumber(adjustmentKcal, 2),
    target,
    finalEnergy: target,
    finalEnergySource: manualGet3
      ? "get_3_manual"
      : get2Suggested
        ? "get2_sugerido"
        : isPediatric
          ? "get_3_pediatrico_pendiente"
          : "sin_get_valido",
    automaticTargetAllowed: !isPediatric,
    requiresProfessionalGet3: isPediatric,
    naf,
    cunningham,
    harrisBenedict,
    get1: {
      cunningham: get1Cunningham,
      harrisBenedict: get1Harris,
      formula: "TMB * NAF",
    },
    adjustments,
    get2: {
      cunningham: get2CunninghamLiteral,
      harrisBenedict: get2HarrisLiteral,
      calculated: calculatedGet2,
      suggested: get2Suggested,
      formula: "GET1 - restriccion_kcal_dia + adicion_kcal_dia",
    },
    get3: {
      manual: manualGet3,
      final: target,
      source: manualGet3
        ? "manual"
        : get2Suggested
          ? "GET2 sugerido"
          : isPediatric
            ? "GET3 pediátrico pendiente"
            : "sin datos suficientes",
      confirmed: manualGet3 > 0,
      warning: manualGet3 > 0 && manualGet3 < 1200 ? "GET3 está por debajo del umbral orientativo configurado; el valor se conserva sin modificar." : "",
    },
    literalNotes,
  };
}

export function calculateMacros(patient) {
  const energy = calculateEnergy(patient);
  const weightReference = positiveNumber(patient.peso_kg) || positiveNumber(patient.peso_ideal_kg) || positiveNumber(patient.peso_objetivo_kg);
  const rule = objectiveRules[patient.objetivo_principal] ?? objectiveRules.mantenimiento;
  const proteinGkg = positiveNumber(patient.proteina_gkg) || rule.proteinGkg;
  const carbsGkg = positiveNumber(patient.carbohidrato_gkg) || rule.carbGkg;
  const fatGkg = positiveNumber(patient.grasa_gkg) || rule.fatGkg || 0;
  const protein = weightReference ? roundNumber(weightReference * proteinGkg, 2) : 0;
  const carbs = weightReference ? roundNumber(weightReference * carbsGkg, 2) : 0;
  const fat = weightReference ? roundNumber(weightReference * fatGkg, 2) : 0;
  const proteinKcal = roundNumber(protein * 4, 2);
  const carbsKcal = roundNumber(carbs * 4, 2);
  const fatKcal = roundNumber(fat * 9, 2);
  const totalMacroKcal = roundNumber(proteinKcal + carbsKcal + fatKcal, 2);
  const activeTimes = activeMealTimes(patient);
  const manualMealCount = Math.round(positiveNumber(patient?.numero_tiempos_comida));
  const mealCount = manualMealCount || activeTimes.length || 0;
  const mealCountMismatch = manualMealCount > 0 && manualMealCount !== activeTimes.length;
  const vctDenominator = Math.max(totalMacroKcal, 1);
  const proteinVct = roundNumber((proteinKcal / vctDenominator) * 100, 1);
  const carbsVct = roundNumber((carbsKcal / vctDenominator) * 100, 1);
  const fatVct = roundNumber((fatKcal / vctDenominator) * 100, 1);
  return {
    calories: energy.target,
    protein,
    proteinGkg: Number(proteinGkg.toFixed(2)),
    carbs,
    carbsGkg: Number(carbsGkg.toFixed(2)),
    fat,
    fatGkg: Number(fatGkg.toFixed(2)),
    proteinKcal,
    carbsKcal,
    fatKcal,
    totalMacroKcal,
    proteinVct,
    carbsVct,
    fatVct,
    carbsVctWordLiteral: carbsVct,
    adequacyKcalPercent: energy.target ? roundNumber((totalMacroKcal / energy.target) * 100, 1) : 0,
    proteinPerMeal: mealCount > 0 ? roundNumber(protein / mealCount, 1) : 0,
    mealCount,
    manualMealCount,
    activeMealCount: activeTimes.length,
    mealCountMismatch,
    weightReference,
    table: [
      { macro: "Proteina", gKg: Number(proteinGkg.toFixed(2)), grams: protein, kcal: proteinKcal, vct: proteinVct },
      { macro: "Grasa", gKg: Number(fatGkg.toFixed(2)), grams: fat, kcal: fatKcal, vct: fatVct },
      { macro: "Carbohidratos", gKg: Number(carbsGkg.toFixed(2)), grams: carbs, kcal: carbsKcal, vct: carbsVct },
    ],
    notes: [
      "El VCT de cada macronutriente se calcula con sus propias kcal, conforme al Excel actualizado.",
      ...(mealCountMismatch ? [`El número manual de tiempos (${manualMealCount}) no coincide con los tiempos activos (${activeTimes.length}); revisa la selección antes de entregar.`] : []),
    ],
  };
}

export function calculateMacroEnergyBalance(patient) {
  const energy = calculateEnergy(patient);
  const weightReference = positiveNumber(patient.peso_kg) || positiveNumber(patient.peso_ideal_kg) || positiveNumber(patient.peso_objetivo_kg);
  const rule = objectiveRules[patient.objetivo_principal] ?? objectiveRules.mantenimiento;
  const proteinGkg = positiveNumber(patient.proteina_gkg) || rule.proteinGkg;
  const fatGkg = positiveNumber(patient.grasa_gkg) || rule.fatGkg;
  const proteinGrams = weightReference ? roundNumber(weightReference * proteinGkg) : 0;
  const fatGrams = weightReference ? roundNumber(weightReference * fatGkg) : 0;
  const fixedKcal = proteinGrams * 4 + fatGrams * 9;
  const remainingKcal = energy.target - fixedKcal;
  const feasible = Boolean(weightReference && energy.target && remainingKcal >= 0);
  const suggestedCarbGkg = feasible
    ? roundNumber(remainingKcal / 4 / weightReference, 2)
    : 0;
  const suggestedCarbGrams = feasible
    ? roundNumber(weightReference * suggestedCarbGkg)
    : 0;
  const suggestedTotalKcal = proteinGrams * 4 + fatGrams * 9 + suggestedCarbGrams * 4;
  return {
    feasible,
    weightReference,
    targetKcal: energy.target,
    proteinGkg,
    fatGkg,
    proteinGrams,
    fatGrams,
    fixedKcal,
    remainingKcal: roundNumber(remainingKcal),
    suggestedCarbGkg,
    suggestedCarbGrams,
    suggestedTotalKcal,
    suggestedAdequacyPercent: energy.target
      ? roundNumber((suggestedTotalKcal / energy.target) * 100, 1)
      : 0,
    reason: !energy.target
      ? "Falta un GET2/GET3 válido."
      : !weightReference
        ? "Falta un peso de referencia."
        : remainingKcal < 0
          ? "Proteína y grasa ya superan el GET; revisa sus valores antes de ajustar carbohidratos."
          : "",
  };
}

export function calculateCurrentIntake(patient) {
  const explicitCalories = Math.round(toNumber(patient.consumo_calorias_actuales));
  const protein = Math.round(toNumber(patient.consumo_proteina_actual_g));
  const carbs = Math.round(toNumber(patient.consumo_carbohidratos_actual_g));
  const fat = Math.round(toNumber(patient.consumo_grasas_actual_g));
  const caloriesFromMacros = Math.round(protein * 4 + carbs * 4 + fat * 9);
  const hasData = [explicitCalories, protein, carbs, fat].some((value) => value > 0);

  return {
    calories: explicitCalories || caloriesFromMacros,
    explicitCalories,
    caloriesFromMacros,
    protein,
    carbs,
    fat,
    hasData,
  };
}

export function validatePatientData(patient) {
  const errors = [];
  const warnings = [];
  const positiveFields = [
    ["peso_kg", "El peso actual"],
    ["talla_cm", "La talla"],
    ["peso_ideal_kg", "El peso ideal"],
    ["peso_objetivo_kg", "El peso objetivo"],
    ["masa_libre_grasa_kg", "La masa libre de grasa"],
    ["naf_valor", "El NAF manual"],
    ["get_3", "El GET3 final"],
    ["proteina_gkg", "La proteína en g/kg"],
    ["grasa_gkg", "La grasa en g/kg"],
    ["carbohidrato_gkg", "El carbohidrato en g/kg"],
  ];
  const nonNegativeFields = [
    ["kg_masa_adiposa_perder", "La masa adiposa a perder", false],
    ["kg_masa_muscular_ganar", "La masa muscular a ganar", false],
    ["entrenamientos_semana", "Los entrenamientos por semana", true],
    ["duracion_entrenamiento_minutos", "La duración del entrenamiento", false],
    ["consumo_calorias_actuales", "Las calorías actuales", false],
    ["consumo_proteina_actual_g", "La proteína actual", false],
    ["consumo_carbohidratos_actual_g", "Los carbohidratos actuales", false],
    ["consumo_grasas_actual_g", "Las grasas actuales", false],
  ];
  for (const [field, label] of positiveFields) {
    addNumericValidationError(errors, patient, field, label, {
      minimum: 0,
      exclusiveMinimum: true,
    });
  }
  for (const [field, label, integer] of nonNegativeFields) {
    addNumericValidationError(errors, patient, field, label, {
      minimum: 0,
      integer,
    });
  }
  for (const [field, label] of [
    ["dias_restriccion", "Los días de restricción"],
    ["numero_tiempos_comida", "El número de tiempos de comida"],
  ]) {
    addNumericValidationError(errors, patient, field, label, {
      minimum: 0,
      exclusiveMinimum: true,
      integer: true,
    });
  }

  const birthDate = parseValidationDate(patient?.fecha_nacimiento);
  const assessmentDate = parseValidationDate(patient?.fecha_evaluacion);
  const today = currentBogotaDate();
  if (hasEnteredValue(patient?.fecha_nacimiento) && !birthDate) {
    errors.push("La fecha de nacimiento no es válida.");
  }
  if (hasEnteredValue(patient?.fecha_evaluacion) && !assessmentDate) {
    errors.push("La fecha de valoración no es válida.");
  }
  if (assessmentDate && today && assessmentDate > today) {
    errors.push("La fecha de valoración no puede estar en el futuro.");
  }
  if (birthDate && (assessmentDate || today) && birthDate > (assessmentDate || today)) {
    errors.push("La fecha de nacimiento no puede ser posterior a la valoración.");
  }

  const anthropometry = calculateAnthropometry(patient);
  if (!hasEnteredValue(patient.peso_kg)) warnings.push("Falta peso actual para calcular requerimientos confiables.");
  if (!hasEnteredValue(patient.talla_cm)) warnings.push("Falta talla para estimar tasa metabólica basal.");
  if (!resolvePatientAge(patient) && !hasEnteredValue(patient.fecha_nacimiento) && !hasEnteredValue(patient.edad)) {
    warnings.push("Falta fecha de nacimiento para calcular la edad y estimar tasa metabólica basal.");
  }
  if (!patientSexKey(patient)) warnings.push("Falta sexo para seleccionar las ecuaciones antropométricas, Harris-Benedict y el NAF sugerido.");
  if (
    !toNumber(patient.peso_ideal_kg)
    && !toNumber(patient.peso_objetivo_kg)
    && !toNumber(anthropometry.derivedBasics?.idealWeightKg)
  ) {
    warnings.push("Falta peso ideal o peso objetivo para Harris-Benedict literal del Word.");
  }
  const energy = calculateEnergy(patient);
  const macros = calculateMacros(patient);
  const currentWeight = positiveNumber(patient.peso_kg);
  const leanMass = positiveNumber(patient.masa_libre_grasa_kg);
  const nafManual = positiveNumber(patient.naf_valor);
  const age = resolvePatientAge(patient);
  if (leanMass && currentWeight && leanMass > currentWeight) {
    warnings.push("La masa libre de grasa supera el peso actual; confirma la fecha y la unidad de ambas medidas.");
  }
  if (nafManual && (nafManual < 1.4 || nafManual > 2.4)) {
    warnings.push("El NAF manual está fuera del rango orientativo 1,40–2,40; se conserva, pero requiere confirmación profesional.");
  }
  if (age > 120) {
    warnings.push("La edad calculada supera 120 años; confirma las fechas registradas.");
  }
  if (patient.population_profile === "adult" && age > 0 && age < 18) {
    warnings.push("La población está configurada como adulta, pero la edad calculada es menor de 18 años.");
  }
  if (patient.population_profile === "pediatric" && age >= 18) {
    warnings.push("La población está configurada como pediátrica, pero la edad calculada es de 18 años o más.");
  }
  if (patient.population_profile === "pediatric" && !energy.get3.confirmed) {
    warnings.push("La población pediátrica requiere un GET3 definido por la profesional; el GET2 adulto se conserva solo como referencia y no se usa como meta automática.");
  }
  if (
    !hasEnteredValue(patient.masa_libre_grasa_kg)
    && !anthropometry.primaryComposition?.leanKg
  ) {
    warnings.push("Falta masa libre de grasa o porcentaje de grasa para Cunningham.");
  }
  if (!patient.objetivo_principal) warnings.push("Selecciona un objetivo principal.");
  if (!patient.estado_nutricional) warnings.push("Define el diagnóstico nutricional antes de cerrar recomendaciones.");
  if (
    !patient.composicion_corporal
    && !toNumber(patient.peso_objetivo_kg)
    && !anthropometry.primaryComposition?.complete
  ) {
    warnings.push("Completa composición corporal o peso objetivo para una mejor personalización.");
  }
  errors.push(...energy.adjustments.errors);
  if (
    energy.adjustments.valid
    && !energy.get3.confirmed
    && (energy.get1.harrisBenedict || energy.get1.cunningham)
    && !energy.get2.suggested
  ) {
    errors.push("El GET2 calculado no es mayor que cero; revisa el ajuste de restricción/adición antes de continuar.");
  }
  if (!energy.get3.confirmed && !energy.get2.suggested) {
    warnings.push("GET3 está pendiente y no hay un GET2 válido para sugerir una meta energética.");
  }
  if (energy.get3.warning) warnings.push(energy.get3.warning);
  if (
    energy.target
    && macros.totalMacroKcal
    && (
      macros.adequacyKcalPercent < macroEnergyAdequacyRange.min
      || macros.adequacyKcalPercent > macroEnergyAdequacyRange.max
    )
  ) {
    warnings.push(`La energía aportada por los macros equivale al ${macros.adequacyKcalPercent}% del GET; ajusta la prescripción antes de entregar.`);
  }
  if (macros.mealCountMismatch) {
    warnings.push(`El número manual de tiempos (${macros.manualMealCount}) no coincide con los tiempos activos (${macros.activeMealCount}).`);
  }
  const complex = `${patient.condiciones ?? ""}`.toLowerCase();
  if (/diabetes|renal|hepat|embarazo|trastorno|tca|cancer/.test(complex)) {
    warnings.push("Caso con condición clínica compleja: requiere revisión profesional especializada.");
  }
  const clinical = `${patient.enfermedades_condiciones ?? ""}`.toLowerCase();
  if (/diabetes|renal|hepat|cardi|hipertension|asma|ansiedad|depresi/.test(clinical)) {
    warnings.push("Condiciones medicas adicionales reportadas: revisar pautas clínicas específicas.");
  }
  const allergyOrIntolerance = getPatientFoodExclusions({
    alergias: patient.alergias,
    intolerancias: patient.intolerancias,
  });
  if (allergyOrIntolerance.length) {
    warnings.push("Alergias / intolerancias reportadas: validar seguridad de sustituciones.");
  }
  if (!patient.nombre) warnings.push("Usa un identificador o nombre del paciente antes de exportar.");
  return {
    valid: errors.length === 0,
    errors: [...new Set(errors)],
    warnings: [...new Set(warnings)],
  };
}

export function validatePatient(patient) {
  const validation = validatePatientData(patient);
  return [...validation.errors, ...validation.warnings];
}

export function activeMealTimes(patient) {
  const manual = normalizePatientMealTimes(patient?.meal_times);
  if (manual.length) return manual;

  const base = ["desayuno", "media_manana", "almuerzo", "media_tarde", "cena"];
  if (toNumber(patient?.entrenamientos_semana) > 0) {
    base.splice(2, 0, "racion_preentrenamiento");
    base.splice(4, 0, "racion_postentrenamiento");
  }
  return base;
}

export const mealTimeCatalog = [
  { id: "desayuno", label: "Desayuno" },
  { id: "media_manana", label: "Media mañana" },
  { id: "racion_preentrenamiento", label: "Ración preentrenamiento" },
  { id: "racion_postentrenamiento", label: "Ración postentrenamiento" },
  { id: "almuerzo", label: "Almuerzo" },
  { id: "media_tarde", label: "Media tarde" },
  { id: "cena", label: "Cena" },
];

const validMealTimeIds = new Set(mealTimeCatalog.map((item) => item.id));

export function defaultMealTimes(patient) {
  return activeMealTimes({
    ...patient,
    meal_times: null,
  });
}

export function normalizePatientMealTimes(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item ?? "").trim())
      .filter((item) => item && validMealTimeIds.has(item));
  }
  const text = String(value ?? "").trim();
  if (!text) return [];
  return text
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter((item) => item && validMealTimeIds.has(item));
}

const optionsByGroup = {
  proteinas: ["huevo", "pollo", "yogurt griego", "lentejas"],
  carbohidratos: ["avena", "arroz integral", "banano", "papa"],
  grasas: ["aguacate", "aceite de oliva", "frutos secos"],
};

const macroGroups = [
  {
    id: "proteinas",
    rowId: "proteina",
    label: "Proteina",
    macroKey: "protein",
    macroLabel: "proteina",
    foodMacroKey: "proteina_100g",
    portionMacroKey: "proteina_g",
    fallbackMacroPerPortion: 25,
    fallbackGrams: 100,
    fallbackMeasure: "1 porcion mediana",
  },
  {
    id: "carbohidratos",
    rowId: "carbo",
    label: "Carbohidrato",
    macroKey: "carbs",
    macroLabel: "carbohidratos",
    foodMacroKey: "carbohidratos_100g",
    portionMacroKey: "carbohidratos_g",
    fallbackMacroPerPortion: 20,
    fallbackGrams: 30,
    fallbackMeasure: "1/2 taza cocida o equivalente",
  },
  {
    id: "grasas",
    rowId: "grasa",
    label: "Grasa saludable",
    macroKey: "fat",
    macroLabel: "grasas",
    foodMacroKey: "grasas_100g",
    portionMacroKey: "grasas_g",
    fallbackMacroPerPortion: 10,
    fallbackGrams: 10,
    fallbackMeasure: "2 cucharaditas",
  },
];

const mealTags = {
  desayuno: ["desayuno"],
  media_manana: ["media_manana", "media manana", "refrigerio", "fruta"],
  racion_preentrenamiento: ["racion_preentrenamiento", "preentrenamiento", "entrenamiento"],
  racion_postentrenamiento: ["racion_postentrenamiento", "postentrenamiento", "recuperacion"],
  almuerzo: ["almuerzo"],
  media_tarde: ["media_tarde", "media tarde", "refrigerio", "fruta"],
  cena: ["cena"],
};

function tableRows(data, tableName) {
  return data?.tables?.[tableName]?.rows ?? [];
}

function isActive(row) {
  return String(row?.estado ?? "activo").toLowerCase() !== "inactivo";
}

function normalizeText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function splitList(value) {
  return String(value ?? "")
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function groupIdFromValue(value) {
  const normalized = normalizeText(value);
  if (normalized.includes("prote")) return "proteinas";
  if (normalized.includes("carbo")) return "carbohidratos";
  if (normalized.includes("grasa")) return "grasas";
  return normalized;
}

function getPortionStandards(data) {
  const standards = Object.fromEntries(macroGroups.map((group) => [
    group.id,
    {
      grams: group.fallbackGrams,
      measure: group.fallbackMeasure,
      macroGrams: group.fallbackMacroPerPortion,
    },
  ]));

  for (const row of tableRows(data, "porciones").filter(isActive)) {
    const groupId = groupIdFromValue(row.grupo_alimento || row.categoria);
    const group = macroGroups.find((item) => item.id === groupId);
    if (!group) continue;
    const macroGrams = toNumber(row[group.portionMacroKey]) || group.fallbackMacroPerPortion;
    standards[group.id] = {
      grams: toNumber(row.gramos) || group.fallbackGrams,
      measure: row.medida_casera || group.fallbackMeasure,
      macroGrams,
      source: row.nombre || row.id,
    };
  }

  return standards;
}

function calculatePortionCount(targetMacro, macroPerPortion) {
  const portions = targetMacro / Math.max(macroPerPortion, 1);
  return Math.max(0.5, Number(portions.toFixed(1)));
}

function mealMatchesFood(food, meal) {
  const haystack = normalizeText(`${food.etiquetas ?? ""} ${food.categoria ?? ""} ${food.nombre ?? ""}`);
  const tags = mealTags[meal] ?? [meal];
  return tags.some((tag) => haystack.includes(normalizeText(tag)));
}

const foodExclusionFamilies = [
  {
    triggers: ["lacteo", "lacteos", "lactosa", "leche"],
    terms: ["lacteo", "lacteos", "lactosa", "leche", "queso", "yogur", "yogurt", "mantequilla", "crema", "suero", "whey"],
  },
  {
    triggers: ["gluten", "trigo"],
    terms: ["gluten", "trigo", "pan", "pasta", "galleta", "sanduche"],
  },
  {
    triggers: ["frutos secos", "nueces", "mani", "almendra"],
    terms: ["frutos secos", "nuez", "nueces", "mani", "almendra", "avellana", "pistacho"],
  },
  {
    triggers: ["pescado", "marisco"],
    terms: ["pescado", "marisco", "camaron", "atun", "salmon", "tilapia"],
  },
  {
    triggers: ["soya", "soja"],
    terms: ["soya", "soja", "tofu"],
  },
  {
    triggers: ["vegetariano", "vegetariana"],
    terms: ["pollo", "carne", "pescado", "cerdo", "pavo", "atun", "salmon", "tilapia"],
  },
  {
    triggers: ["vegano", "vegana"],
    terms: ["pollo", "carne", "pescado", "cerdo", "pavo", "atun", "salmon", "tilapia", "huevo", "leche", "queso", "yogur", "yogurt", "mantequilla", "whey"],
  },
];

function isNegativeClinicalListEntry(value) {
  const normalized = normalizeText(value)
    .replace(/[.;,:]+$/g, "")
    .trim();
  return [
    /^(?:no|na|n\/a|ningun[ao]s?)$/,
    /^ningun[ao]s?\s+(?:conocid[ao]s?|reportad[ao]s?|identificad[ao]s?)$/,
    /^no\s+(?:aplica|refiere|reporta|presenta|tiene|hay)(?:\s+(?:alergias?|intolerancias?|restricciones?))?(?:\s+conocid[ao]s?)?$/,
    /^sin\s+(?:alergias?|intolerancias?|restricciones?)(?:\s+(?:conocid[ao]s?|reportad[ao]s?|identificad[ao]s?))?$/,
  ].some((pattern) => pattern.test(normalized));
}

export function getPatientFoodExclusions(patient) {
  return splitList([
    patient?.alimentos_no_consume,
    patient?.alergias,
    patient?.intolerancias,
    patient?.restricciones,
    patient?.restricciones_culturales,
  ].map((value) => String(value ?? "").trim()).filter(Boolean).join(","))
    .filter((item) => (
      /[a-z0-9]/i.test(normalizeText(item))
      && !isNegativeClinicalListEntry(item)
    ));
}

export function getPatientFoodPreferences(patient) {
  return splitList([
    patient?.alimentos_preferidos,
    patient?.preferencias_alimentarias,
  ].map((value) => String(value ?? "").trim()).filter(Boolean).join(","))
    .filter((item) => /[a-z0-9]/i.test(normalizeText(item)));
}

function normalizedTextContains(haystack, needle) {
  const safeNeedle = normalizeText(needle);
  if (!safeNeedle) return false;
  const escaped = safeNeedle
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/\s+/g, "\\s+");
  const pluralSuffix = safeNeedle.endsWith("s") ? "" : "(?:s|es)?";
  return new RegExp(`(^|[^a-z0-9])${escaped}${pluralSuffix}(?=$|[^a-z0-9])`).test(normalizeText(haystack));
}

function exclusionTerms(value) {
  const normalized = normalizeText(value);
  if (!normalized) return [];
  const withoutPrefix = normalized
    .replace(
      /^(?:sin|evitar|no\s+(?:consume|consumir|come|comer|tolera)|alerg(?:ia|ico|ica)\s+(?:a|al|a la)|intolerancia\s+(?:a|al|a la))\s+/,
      "",
    )
    .trim();
  return [
    normalized,
    withoutPrefix,
    ...withoutPrefix.split(/[/\n]|\s+(?:y|e)\s+/),
  ].map((item) => item.trim()).filter((item) => item.length > 1);
}

function expandedPatientFoodExclusions(patient) {
  const reported = getPatientFoodExclusions(patient).flatMap(exclusionTerms);
  const expanded = new Set(reported);
  for (const family of foodExclusionFamilies) {
    if (reported.some((item) => family.triggers.some((trigger) => normalizedTextContains(item, trigger)))) {
      family.terms.forEach((term) => expanded.add(normalizeText(term)));
    }
  }
  return [...expanded];
}

function textAllowedForPatient(value, patient) {
  const text = normalizeText(value);
  if (!text) return true;
  return !expandedPatientFoodExclusions(patient)
    .some((exclusion) => normalizedTextContains(text, exclusion));
}

const foodPreferenceStopWords = new Set([
  "alimento",
  "alimentos",
  "comida",
  "comidas",
  "prefiere",
  "preferido",
  "preferidos",
  "preferida",
  "preferidas",
  "consumir",
  "consume",
  "gusta",
  "gustan",
]);

function expandedPatientFoodPreferences(patient) {
  const terms = new Set();
  for (const preference of getPatientFoodPreferences(patient)) {
    const normalized = normalizeText(preference);
    if (!normalized) continue;
    terms.add(normalized);
    normalized
      .split(/\s+/)
      .filter((token) => token.length >= 4 && !foodPreferenceStopWords.has(token))
      .forEach((token) => terms.add(token));
  }
  return [...terms];
}

function foodPreferenceScore(value, patient) {
  const text = normalizeText(value);
  return expandedPatientFoodPreferences(patient)
    .reduce((score, preference) => score + (normalizedTextContains(text, preference) ? 1 : 0), 0);
}

function foodAllowedForPatient(food, patient) {
  const foodText = normalizeText(`${food.nombre ?? ""} ${food.categoria ?? ""} ${food.restricciones ?? ""}`);
  return textAllowedForPatient(foodText, patient);
}

function foodsForGroup(data, patient, groupId, meal) {
  const rows = tableRows(data, "alimentos")
    .filter(isActive)
    .filter((food) => {
      const foodGroup = groupIdFromValue(food.grupo_alimento || food.categoria);
      return foodGroup === groupId || (foodGroup === "mixto" && groupId !== "grasas");
    })
    .filter((food) => foodAllowedForPatient(food, patient))
    .sort((a, b) => {
      const aPreference = foodPreferenceScore(`${a.nombre ?? ""} ${a.etiquetas ?? ""}`, patient);
      const bPreference = foodPreferenceScore(`${b.nombre ?? ""} ${b.etiquetas ?? ""}`, patient);
      if (aPreference !== bPreference) return bPreference - aPreference;
      const aMeal = mealMatchesFood(a, meal) ? 0 : 1;
      const bMeal = mealMatchesFood(b, meal) ? 0 : 1;
      if (aMeal !== bMeal) return aMeal - bMeal;
      return (toNumber(a.prioridad) || 99) - (toNumber(b.prioridad) || 99);
    });

  return rows;
}

function templateOptions(data, meal, groupId) {
  const template = tableRows(data, "plantillas_esquema_diario")
    .filter(isActive)
    .find((row) => normalizeText(row.tiempo_comida) === normalizeText(meal)
      && groupIdFromValue(row.grupo_macro) === groupId);
  return template ? splitList(template.opciones) : [];
}

function formatPortionCount(value) {
  return Number(value.toFixed(1)).toLocaleString("es-CO");
}

function formatPortionLabel(value) {
  return `${formatPortionCount(value)} ${Number(value.toFixed(1)) === 1 ? "porcion" : "porciones"}`;
}

function roundGrams(value) {
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (value < 20) return Math.round(value);
  return Math.round(value / 5) * 5;
}

function buildOptionsText(data, patient, groupId, meal) {
  const foods = foodsForGroup(data, patient, groupId, meal);
  const fromTemplates = templateOptions(data, meal, groupId);
  const fromFoods = foods.map((food) => food.nombre);
  const seen = new Set();
  const merged = [];
  for (const option of [...fromTemplates, ...fromFoods, ...optionsByGroup[groupId]]) {
    const key = normalizeText(option);
    if (!key || seen.has(key) || !textAllowedForPatient(option, patient)) continue;
    seen.add(key);
    merged.push(option);
  }
  return merged.slice(0, 6).join(", ");
}

const foodNutrientKeys = {
  protein: "proteina_100g",
  carbs: "carbohidratos_100g",
  fat: "grasas_100g",
};

function foodNutritionForGrams(food, grams) {
  const factor = grams / 100;
  const protein = toNumber(food?.proteina_100g) * factor;
  const carbs = toNumber(food?.carbohidratos_100g) * factor;
  const fat = toNumber(food?.grasas_100g) * factor;
  return {
    kcal: Number((toNumber(food?.calorias_100g) * factor || protein * 4 + carbs * 4 + fat * 9).toFixed(1)),
    protein: Number(protein.toFixed(1)),
    carbs: Number(carbs.toFixed(1)),
    fat: Number(fat.toFixed(1)),
  };
}

function mealFoodSelectionKey(day, meal, groupId) {
  return `${day}:${meal}:${groupId}`;
}

function selectMealFoods(data, patient, mealDistribution, standards, {
  day = 1,
  foodOverrides = {},
  selectionCache = null,
} = {}) {
  const meal = mealDistribution.meal;
  const overrideSignature = macroGroups
    .map((group) => String(
      foodOverrides[mealFoodSelectionKey(day, meal, group.id)] ?? "",
    ).trim())
    .join("|");
  const cacheKey = `${meal}|${overrideSignature}`;
  const cachedRanking = selectionCache?.get(cacheKey);
  if (cachedRanking) {
    const cachedPool = cachedRanking.excellent.length
      ? cachedRanking.excellent
      : cachedRanking.ranked.slice(0, 1);
    return cachedPool.length
      ? cachedPool[Math.max(0, Number(day) - 1) % cachedPool.length].selection
      : [];
  }
  const candidateGroups = macroGroups.map((group) => {
    const candidates = foodsForGroup(data, patient, group.id, meal);
    const overrideId = String(
      foodOverrides[mealFoodSelectionKey(day, meal, group.id)] ?? "",
    ).trim();
    const explicitFood = overrideId
      ? candidates.find((food) => String(food.id ?? "") === overrideId)
      : null;
    const mealCandidates = candidates.filter((food) => mealMatchesFood(food, meal));
    const automaticCandidates = (mealCandidates.length ? mealCandidates : candidates)
      .filter((food) => toNumber(food[group.foodMacroKey]) > 0)
      .slice(0, 4);
    const choices = explicitFood
      ? [explicitFood]
      : automaticCandidates.length
        ? automaticCandidates
        : candidates.slice(0, 1);
    const options = candidates.map((food) => ({
      id: String(food.id ?? ""),
      name: String(food.nombre ?? ""),
      measure: String(food.medida_casera ?? ""),
      servingGrams: toNumber(food.porcion_estandar_g),
      sourceId: String(food.fuente_id ?? ""),
      sourceUrl: String(food.fuente_url ?? ""),
    })).filter((food) => food.id && food.name);
    return choices.map((food) => ({
      group,
      food,
      options,
      selectedByOverride: Boolean(explicitFood),
    })).concat(choices.length ? [] : [{
      group,
      food: undefined,
      options,
      selectedByOverride: false,
    }]);
  });

  const combinations = [];
  const visit = (groupIndex, selection, identities) => {
    if (groupIndex >= candidateGroups.length) {
      combinations.push(selection);
      return;
    }
    for (const candidate of candidateGroups[groupIndex]) {
      const identity = candidate.food?.id || normalizeText(candidate.food?.nombre);
      if (identity && identities.has(identity)) continue;
      const nextIdentities = new Set(identities);
      if (identity) nextIdentities.add(identity);
      visit(groupIndex + 1, [...selection, candidate], nextIdentities);
    }
  };
  visit(0, [], new Set());

  const ranked = combinations.map((selection) => {
    const solution = solveMealFoodQuantities(selection, mealDistribution, standards);
    const adequacy = Object.values(solution.adequacy);
    return {
      selection,
      score: adequacy.reduce((sum, value) => sum + Math.abs(value - 100), 0),
      excellent: adequacy.every((value) => value >= 95 && value <= 105),
    };
  }).sort((a, b) => a.score - b.score);
  const excellent = ranked.filter((candidate) => candidate.excellent);
  selectionCache?.set(cacheKey, { ranked, excellent });
  const pool = excellent.length ? excellent : ranked.slice(0, 1);
  return pool.length
    ? pool[Math.max(0, Number(day) - 1) % pool.length].selection
    : candidateGroups.map((group) => group[0]).filter(Boolean);
}

function solveMealFoodQuantities(selections, mealDistribution, standards) {
  const macroKeys = ["protein", "carbs", "fat"];
  const targets = macroKeys.map((key) => toNumber(mealDistribution[key]));
  const hasCompleteNutrition = selections.every(({ food }) => (
    food && macroKeys.some((key) => toNumber(food[foodNutrientKeys[key]]) > 0)
  ));
  let rawGrams;

  if (hasCompleteNutrition && targets.every((target) => target > 0)) {
    const coefficients = macroKeys.map((macroKey, macroIndex) => selections.map(({ food }) => (
      toNumber(food[foodNutrientKeys[macroKey]]) / 100 / targets[macroIndex]
    )));
    rawGrams = selections.map(() => 0);
    for (let iteration = 0; iteration < 32; iteration += 1) {
      for (let column = 0; column < selections.length; column += 1) {
        let numerator = 0;
        let denominator = 0;
        for (let row = 0; row < macroKeys.length; row += 1) {
          const coefficient = coefficients[row][column];
          const contributionFromOthers = rawGrams.reduce((sum, grams, index) => (
            index === column ? sum : sum + coefficients[row][index] * grams
          ), 0);
          numerator += coefficient * (1 - contributionFromOthers);
          denominator += coefficient * coefficient;
        }
        rawGrams[column] = denominator > 0 ? Math.max(0, numerator / denominator) : 0;
      }
    }
  } else {
    rawGrams = selections.map(({ group, food }) => {
      const standard = standards[group.id];
      const referenceGrams = toNumber(food?.porcion_estandar_g) || standard.grams;
      const nutrientPer100g = toNumber(food?.[group.foodMacroKey]);
      const macroPerPortion = nutrientPer100g > 0
        ? nutrientPer100g * referenceGrams / 100
        : standard.macroGrams;
      return referenceGrams * calculatePortionCount(
        toNumber(mealDistribution[group.macroKey]),
        macroPerPortion,
      );
    });
  }

  const grams = rawGrams.map((value) => roundGrams(Math.min(value, 800)));
  const nutritionRows = selections.map(({ food }, index) => foodNutritionForGrams(food, grams[index]));
  const totals = Object.fromEntries(["kcal", ...macroKeys].map((key) => [
    key,
    Number(nutritionRows.reduce((sum, nutrition) => sum + nutrition[key], 0).toFixed(1)),
  ]));
  const adequacy = Object.fromEntries(macroKeys.map((key) => [
    key,
    mealDistribution[key] > 0
      ? Number((totals[key] / mealDistribution[key] * 100).toFixed(1))
      : 0,
  ]));

  return {
    grams,
    nutritionRows,
    totals,
    adequacy,
    withinTolerance: macroKeys.every((key) => adequacy[key] >= 80 && adequacy[key] <= 120),
    quantifiedFromFoods: hasCompleteNutrition,
  };
}

export function distributePortions(patient, data = null) {
  const macros = calculateMacros(patient);
  const meals = activeMealTimes(patient);
  const standards = getPortionStandards(data);
  const weights = {
    desayuno: 0.2,
    media_manana: 0.1,
    racion_preentrenamiento: 0.1,
    racion_postentrenamiento: 0.12,
    almuerzo: 0.28,
    media_tarde: 0.1,
    cena: 0.2,
  };
  const totalWeight = meals.reduce((sum, meal) => sum + (weights[meal] ?? 0.1), 0);
  return meals.map((meal) => {
    const share = (weights[meal] ?? 0.1) / totalWeight;
    return {
      meal,
      kcal: Math.round(macros.calories * share),
      macroKcal: Math.round(macros.totalMacroKcal * share),
      protein: Math.round(macros.protein * share),
      carbs: Math.round(macros.carbs * share),
      fat: Math.round(macros.fat * share),
      portions: {
        proteinas: calculatePortionCount(macros.protein * share, standards.proteinas.macroGrams),
        carbohidratos: calculatePortionCount(macros.carbs * share, standards.carbohidratos.macroGrams),
        grasas: calculatePortionCount(macros.fat * share, standards.grasas.macroGrams),
      },
    };
  });
}

export function mealLabel(key) {
  return {
    desayuno: "Desayuno",
    media_manana: "Media mañana",
    racion_preentrenamiento: "Racion preentrenamiento",
    racion_postentrenamiento: "Racion postentrenamiento",
    almuerzo: "Almuerzo",
    media_tarde: "Media tarde",
    cena: "Cena",
  }[key] ?? key;
}

function buildMealSchemeRows(patient, data, item, selectionOptions = {}) {
  const standards = getPortionStandards(data);
  const selections = selectMealFoods(data, patient, item, standards, selectionOptions);
  const solution = solveMealFoodQuantities(selections, item, standards);
  return selections.map(({
    group,
    food,
    options,
    selectedByOverride,
  }, index) => {
    const standard = standards[group.id];
    const grams = solution.grams[index];
    const referenceGrams = toNumber(food?.porcion_estandar_g) || standard.grams;
    const portionCount = Number((grams / Math.max(referenceGrams, 1)).toFixed(1));
    const measure = food?.medida_casera || standard.measure;
    const foodName = food?.nombre || group.label.toLowerCase();
    const targetMacro = Math.round(item[group.macroKey]);
    const estimatedPrimaryMacro = solution.nutritionRows[index][group.macroKey];
    const source = standard.source ? ` Base: ${standard.source}.` : "";
    const exampleText = grams > 0
      ? `${formatPortionLabel(portionCount)} = ${grams} g aprox (${measure} por porcion) de ${foodName}. Aporte propio: ${estimatedPrimaryMacro} g de ${group.macroLabel}; objetivo combinado del tiempo: ${targetMacro} g.${source}`
      : `No requiere una porción adicional de ${foodName}: el objetivo de ${group.macroLabel} se cubre con los demás alimentos del tiempo.${source}`;
    return {
      id: `${item.meal}_${group.rowId}`,
      meal: item.meal,
      tiempo: mealLabel(item.meal),
      grupo: group.label,
      groupId: group.id,
      macroKey: group.macroKey,
      opciones: buildOptionsText(data, patient, group.id, item.meal),
      ejemplo: exampleText,
      porciones: portionCount,
      foodId: food?.id || "",
      foodName,
      foodOptions: options,
      foodSource: food?.fuente || "",
      foodSourceId: food?.fuente_id || "",
      foodSourceUrl: food?.fuente_url || "",
      foodSourceDescription: food?.descripcion_fuente || "",
      foodCategory: food?.categoria || "",
      foodTags: food?.etiquetas || "",
      selectedByOverride,
      grams,
      measure,
      targetMacro,
      estimatedPrimaryMacro,
      primaryMacroDifference: Number((estimatedPrimaryMacro - targetMacro).toFixed(1)),
      nutrition: solution.nutritionRows[index],
      mealTotals: solution.totals,
      mealAdequacy: solution.adequacy,
      mealWithinTolerance: solution.withinTolerance,
      quantifiedFromFood: solution.quantifiedFromFoods,
    };
  });
}

export function buildDailyScheme(patient, data = null) {
  return distributePortions(patient, data)
    .flatMap((item) => buildMealSchemeRows(patient, data, item));
}

export function evaluateDailyScheme(scheme, macros) {
  const targetByMacro = {
    protein: toNumber(macros?.protein),
    carbs: toNumber(macros?.carbs),
    fat: toNumber(macros?.fat),
  };
  const rows = Object.entries(targetByMacro).map(([macroKey, target]) => {
    const estimated = Number((scheme ?? [])
      .reduce((sum, row) => sum + toNumber(row.nutrition?.[macroKey]), 0)
      .toFixed(1));
    const adequacyPercent = target > 0 ? Number((estimated / target * 100).toFixed(1)) : 0;
    return {
      macroKey,
      target,
      estimated,
      adequacyPercent,
      withinTolerance: target > 0 && adequacyPercent >= 95 && adequacyPercent <= 105,
    };
  });
  return {
    rows,
    complete: rows.every((row) => row.target > 0),
    valid: rows.every((row) => row.withinTolerance),
  };
}

const menuTemplates = {
  desayuno: [
    "Avena cocida con banano y huevo",
    "Yogur griego natural con avena y fruta",
    "Huevos pericos con arepa y aguacate",
    "Arepa con pollo desmechado, tomate y aguacate",
    "Avena nocturna con yogur natural, fruta y semillas de chía",
    "Avena cocida en bebida vegetal con fruta y semillas de chía",
    "Arepa con tofu revuelto, tomate y aguacate",
  ],
  media_manana: [
    "Yogur griego con fruta",
    "Banano con frutos secos",
    "Tostadas de arroz con queso bajo en grasa",
    "Fruta con semillas de chía",
    "Huevo cocido con arepa",
    "Avena fría con bebida vegetal y canela",
    "Hummus con bastones de vegetales",
  ],
  racion_preentrenamiento: [
    "Banano y agua",
    "Avena ligera con fruta",
    "Tostadas de arroz con mermelada baja en azúcar",
    "Arepa pequeña con fruta y agua",
    "Papa cocida con fruta y agua",
    "Batido de bebida vegetal, avena y banano",
    "Compota de fruta sin azúcar con tostadas de arroz",
  ],
  racion_postentrenamiento: [
    "Yogur griego con cereal",
    "Batido proteico con fruta",
    "Sándwich de pollo y fruta",
    "Huevos con arepa y fruta",
    "Bowl de lentejas con arroz y vegetales",
    "Atún con papa y vegetales",
    "Tofu con arroz y vegetales",
  ],
  almuerzo: [
    "Pollo a la plancha con arroz integral, ensalada y aguacate",
    "Bowl de lentejas con arroz, vegetales y aceite de oliva",
    "Pescado con papa, vegetales y aguacate",
    "Carne magra con arroz, ensalada y aguacate",
    "Garbanzos guisados con arroz y vegetales",
    "Pavo salteado con papa, vegetales y aceite de oliva",
    "Tofu salteado con arroz, vegetales y aguacate",
  ],
  media_tarde: [
    "Fruta con yogurt",
    "Arepa pequeña con huevo",
    "Avena fría con canela",
    "Yogur natural con fruta y semillas de chía",
    "Hummus con arepa y vegetales",
    "Banano con mantequilla de maní",
    "Fruta con tostadas de arroz",
  ],
  cena: [
    "Tortilla de huevo con vegetales y aguacate",
    "Pollo guisado con ensalada y arroz",
    "Lentejas con ensalada y huevo",
    "Pescado al horno con papa y vegetales",
    "Ensalada de garbanzos con arepa y aguacate",
    "Carne magra salteada con vegetales y arroz",
    "Tofu con vegetales, arroz y aguacate",
  ],
};

function cleanSentenceFragment(value) {
  return String(value ?? "").trim().replace(/[.\s]+$/g, "");
}

function mealTargetText(distribution) {
  if (!distribution?.kcal) return "";
  const macroKcal = distribution.macroKcal
    || Math.round(distribution.protein * 4 + distribution.carbs * 4 + distribution.fat * 9);
  const getReference = Math.abs(distribution.kcal - macroKcal) > 5
    ? ` Referencia proporcional del GET: ${distribution.kcal} kcal.`
    : "";
  return `Objetivo del tiempo por macros: ${macroKcal} kcal · ${distribution.protein} g proteína · ${distribution.carbs} g carbohidratos · ${distribution.fat} g grasa.${getReference}`;
}

function menuTemplateText(row, distribution) {
  const preparation = cleanSentenceFragment(row.preparacion_sugerida || row.nombre || "Preparacion editable");
  const parts = [
    `${preparation}.`,
    row.alimentos ? `Alimentos: ${cleanSentenceFragment(row.alimentos)}.` : "",
    row.observaciones ? `Observaciones: ${cleanSentenceFragment(row.observaciones)}.` : "",
    mealTargetText(distribution),
  ].filter(Boolean);
  return `${parts.join("\n")}\nCantidades: usar la base cuantificada calculada para este tiempo.`;
}

function menuTemplateSourceText(row) {
  return [
    row?.preparacion_sugerida,
    row?.nombre,
    row?.alimentos,
    row?.cantidades,
    row?.medida_casera,
    row?.observaciones,
  ].filter(Boolean).join(" ");
}

function menuTemplateRows(data, patient, meal) {
  return tableRows(data, "plantillas_menu_7_dias")
    .filter(isActive)
    .filter((row) => normalizeText(row.tiempo_comida) === normalizeText(meal))
    .filter((row) => textAllowedForPatient(menuTemplateSourceText(row), patient));
}

function findMenuTemplate(data, patient, meal, dayIndex) {
  return menuTemplateRows(data, patient, meal)
    .find((row) => toNumber(row.dia) === dayIndex + 1) ?? null;
}

function menuFallbackCandidates(data, patient, meal) {
  const rows = menuTemplateRows(data, patient, meal);
  const explicitTemplateKeys = new Set(
    rows
      .filter((row) => toNumber(row.dia) >= 1 && toNumber(row.dia) <= 7)
      .map((row) => normalizeText(row.preparacion_sugerida || row.nombre)),
  );
  const candidates = [
    ...rows
      .filter((row) => toNumber(row.dia) < 1 || toNumber(row.dia) > 7)
      .map((row) => ({
        source: menuTemplateSourceText(row),
        text: menuTemplateText(row),
      })),
    ...(menuTemplates[meal] ?? ["Preparación editable"])
      .filter((template) => textAllowedForPatient(template, patient))
      .filter((template) => !explicitTemplateKeys.has(normalizeText(template)))
      .map((template) => ({
        source: template,
        text: `${template}. Ajustar cantidades según el esquema diario.`,
      })),
  ];
  const seen = new Set();
  return candidates
    .filter((candidate) => {
      const key = normalizeText(candidate.source);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((candidate, index) => ({
      ...candidate,
      index,
      preferenceScore: foodPreferenceScore(candidate.source, patient),
    }))
    .sort((a, b) => b.preferenceScore - a.preferenceScore || a.index - b.index);
}

function fallbackMenuIndex(data, patient, meal, dayIndex) {
  const explicitDays = new Set(
    menuTemplateRows(data, patient, meal)
      .map((row) => toNumber(row.dia))
      .filter((day) => day >= 1 && day <= 7),
  );
  let index = 0;
  for (let previousDay = 1; previousDay <= dayIndex; previousDay += 1) {
    if (!explicitDays.has(previousDay)) index += 1;
  }
  return index;
}

function safeMenuFallback(data, patient, meal) {
  const foods = macroGroups
    .map((group) => foodsForGroup(data, patient, group.id, meal)[0]?.nombre)
    .filter(Boolean);
  const uniqueFoods = [...new Map(foods.map((food) => [normalizeText(food), food])).values()];
  if (uniqueFoods.length) {
    return `Combinación de ${uniqueFoods.join(", ")}. Ajustar cantidades según esquema diario y tolerancia individual.`;
  }
  return "Preparación individualizada pendiente de sustitución segura. Definir alimentos y cantidades antes de entregar.";
}

function joinNaturalList(values) {
  const clean = values.filter(Boolean);
  if (clean.length <= 1) return clean[0] ?? "";
  return `${clean.slice(0, -1).join(", ")} y ${clean.at(-1)}`;
}

function quantifiedMenuText(calculatedPortions, distribution) {
  const foods = calculatedPortions
    .filter((row) => row.grams > 0)
    .map((row) => `${row.foodName} (${row.grams} g)`);
  return [
    foods.length
      ? `Preparación base: ${joinNaturalList(foods)}.`
      : "Preparación pendiente de cuantificación profesional.",
    "La técnica culinaria y los condimentos pueden personalizarse sin cambiar estos alimentos ni sus cantidades.",
    mealTargetText(distribution),
  ].filter(Boolean).join("\n");
}

export function generateMenu(patient, data = null, {
  foodOverrides = {},
} = {}) {
  const meals = activeMealTimes(patient);
  const hasFoodData = tableRows(data, "alimentos").filter(isActive).length > 0;
  const schemeByMeal = new Map();
  if (!hasFoodData) {
    for (const row of buildDailyScheme(patient, data)) {
      if (!schemeByMeal.has(row.meal)) schemeByMeal.set(row.meal, []);
      schemeByMeal.get(row.meal).push(row);
    }
  }
  const distributionByMeal = new Map(
    distributePortions(patient, data).map((distribution) => [distribution.meal, distribution]),
  );
  const macroTargets = calculateMacros(patient);
  const selectionCache = new Map();
  return Array.from({ length: 7 }, (_, dayIndex) => {
    const day = dayIndex + 1;
    const generatedMeals = meals.map((meal) => {
      const editableTemplate = findMenuTemplate(data, patient, meal, dayIndex);
      const distribution = distributionByMeal.get(meal);
      const calculatedPortions = hasFoodData
        ? buildMealSchemeRows(patient, data, distribution, {
          day,
          foodOverrides,
          selectionCache,
        })
        : schemeByMeal.get(meal) ?? [];
      const calculatedMeal = calculatedPortions[0];
      const calculatedBase = {
        meal,
        label: mealLabel(meal),
        target: distribution ? {
          kcal: distribution.kcal,
          macroKcal: distribution.macroKcal,
          protein: distribution.protein,
          carbs: distribution.carbs,
          fat: distribution.fat,
        } : null,
        calculatedPortions,
        estimated: calculatedMeal?.mealTotals ?? null,
        adequacy: calculatedMeal?.mealAdequacy ?? null,
        quantifiedWithinTolerance: Boolean(calculatedMeal?.mealWithinTolerance),
      };
      if (hasFoodData) {
        return {
          ...calculatedBase,
          text: quantifiedMenuText(calculatedPortions, distribution),
        };
      }
      if (editableTemplate) {
        return {
          ...calculatedBase,
          text: menuTemplateText(editableTemplate, distribution),
        };
      }
      const candidates = menuFallbackCandidates(data, patient, meal);
      if (!candidates.length) {
        return {
          ...calculatedBase,
          text: `${safeMenuFallback(data, patient, meal)}\n${mealTargetText(distribution)}`.trim(),
        };
      }
      const candidate = candidates[fallbackMenuIndex(data, patient, meal, dayIndex) % candidates.length];
      return {
        ...calculatedBase,
        text: `${candidate.text}\n${mealTargetText(distribution)}`.trim(),
      };
    });
    const validation = hasFoodData
      ? evaluateDailyScheme(
        generatedMeals.flatMap((meal) => meal.calculatedPortions),
        macroTargets,
      )
      : { rows: [], complete: false, valid: false };
    return {
      day,
      meals: generatedMeals,
      validation,
    };
  });
}

export function findMenuSafetyConflicts(menu, patient) {
  const exclusions = expandedPatientFoodExclusions(patient);
  if (!exclusions.length) return [];
  return (menu ?? []).flatMap((day) => (day.meals ?? []).flatMap((meal) => {
    const matches = [...new Set(
      exclusions.filter((exclusion) => normalizedTextContains(meal.text, exclusion)),
    )].sort((a, b) => a.length - b.length);
    if (!matches.length) return [];
    return [{
      day: day.day,
      meal: meal.meal,
      label: meal.label || mealLabel(meal.meal),
      terms: matches,
    }];
  }));
}

function appliesToPatient(value, patient) {
  const tokens = splitList(value).map(normalizeText).filter(Boolean);
  if (!tokens.length || tokens.includes("todos")) return true;
  const objective = normalizeText(patient?.objetivo_principal);
  const objectiveLabel = normalizeText(objectiveRules[patient?.objetivo_principal]?.label);
  const isAthlete = toNumber(patient?.entrenamientos_semana) > 0 || Boolean(String(patient?.deporte ?? "").trim());
  const patientText = normalizeText([
    patient?.objetivo_secundario,
    patient?.estado_nutricional,
    patient?.condiciones,
    patient?.enfermedades_condiciones,
    patient?.restricciones,
    patient?.intolerancias,
    patient?.alergias,
  ].filter(Boolean).join(" "));

  return tokens.some((token) => {
    if (token === objective || token === objectiveLabel) return true;
    if (token === "deportista" && isAthlete) return true;
    return patientText.includes(token);
  });
}

function sortedRecommendationRows(data, tableName, orderKey = "orden") {
  return tableRows(data, tableName)
    .filter(isActive)
    .sort((a, b) => {
      const orderA = toNumber(a[orderKey] ?? a.prioridad) || 99;
      const orderB = toNumber(b[orderKey] ?? b.prioridad) || 99;
      return orderA - orderB || String(a.nombre ?? "").localeCompare(String(b.nombre ?? ""), "es");
    });
}

function interpolateRecommendationText(text, context) {
  return String(text ?? "").replace(/\{([a-zA-Z0-9_]+)\}/g, (_, key) => context[key] ?? "");
}

function pushRecommendation(recs, id, title, text) {
  const safeText = String(text ?? "").trim();
  if (!safeText) return;
  recs.push({
    id,
    title: title || "Recomendacion",
    text: safeText,
  });
}

function patientContextText(patient) {
  return normalizeText([
    patient?.restricciones,
    patient?.intolerancias,
    patient?.alergias,
    patient?.preferencias_alimentarias,
    patient?.alimentos_no_consume,
    patient?.restricciones_culturales,
    patient?.condiciones,
    patient?.enfermedades_condiciones,
  ].filter(Boolean).join(" "));
}

function rowMatchesPatient(row, patient) {
  const patientText = patientContextText(patient);
  if (!patientText) return false;
  const ignoredIdentityWords = new Set([
    "alimentaria",
    "alimentario",
    "alergia",
    "condicion",
    "enfermedad",
    "intolerancia",
    "restriccion",
  ]);
  const identity = normalizeText(row.nombre);
  const identityTerms = new Set([
    identity,
    ...identity
      .split(/\s+/)
      .filter((token) => token.length > 4 && !ignoredIdentityWords.has(token)),
  ]);
  return [...identityTerms].some((term) => normalizedTextContains(patientText, term));
}

function formatRestrictionRecommendations(data, patient) {
  const matches = tableRows(data, "restricciones_alimentarias")
    .filter(isActive)
    .filter((row) => rowMatchesPatient(row, patient));
  const reported = getPatientFoodExclusions(patient).join("; ");
  const parts = matches.map((row) => {
    const avoid = row.alimentos_a_evitar ? `evitar o revisar ${row.alimentos_a_evitar}` : "";
    const substitutes = row.sustitutos ? `sustituir por ${row.sustitutos}` : "";
    const alert = row.alerta ? row.alerta : "";
    return `${row.nombre}: ${[avoid, substitutes, alert].filter(Boolean).map(cleanSentenceFragment).join(". ")}.`;
  });
  if (reported && !parts.length) {
    parts.push(`Restricciones reportadas: ${reported}. Validar sustituciones, etiquetas y tolerancia antes de entregar.`);
  }
  return parts.join(" ");
}

function formatClinicalConditionRecommendations(data, patient) {
  return tableRows(data, "enfermedades_condiciones")
    .filter(isActive)
    .filter((row) => rowMatchesPatient(row, patient))
    .map((row) => `${row.nombre}: ${cleanSentenceFragment(row.alerta || row.descripcion)}.${row.derivacion ? ` Derivar o coordinar con ${row.derivacion}.` : ""}`)
    .join(" ");
}

function formatFatsAndTechniques(data) {
  const fats = tableRows(data, "grasas_recomendadas")
    .filter(isActive)
    .slice(0, 4)
    .map((row) => `${row.nombre} (${row.porcion || "porcion medida"}): ${cleanSentenceFragment(row.uso || row.descripcion)}`);
  const techniques = tableRows(data, "tecnicas_culinarias")
    .filter(isActive)
    .slice(0, 4)
    .map((row) => `${row.nombre}: ${cleanSentenceFragment(row.recomendacion || row.descripcion)}`);
  return [
    fats.length ? `Grasas recomendadas: ${fats.join("; ")}.` : "",
    techniques.length ? `Tecnicas culinarias: ${techniques.join("; ")}.` : "",
  ].filter(Boolean).join(" ");
}

function fallbackRecommendations(patient, meals, objective) {
  const reportedRestrictions = getPatientFoodExclusions(patient).join("; ");
  return [
    {
      id: "organizacion",
      title: "Organizacion del plan",
      text: "Este plan de alimentación está organizado para seleccionar por tiempo de comida un alimento fuente de proteína, carbohidratos y grasas saludables. De cada alimento fuente se presentan varias opciones para variar la alimentación cada día.",
    },
    {
      id: "tiempos",
      title: "Tiempos de comida",
      text: `El plan usa ${meals.length} tiempos de comida para apoyar el objetivo de ${objective.toLowerCase()} y adaptarse a horarios de entrenamiento y rutina diaria.`,
    },
    {
      id: "desayuno_raciones",
      title: "Desayuno y raciones",
      text: "El desayuno integra una fuente de proteína, carbohidrato y grasa saludable. Las raciones pre o postentrenamiento se activan cuando hay entrenamiento reportado.",
    },
    {
      id: "hidratacion_dia",
      title: "Hidratacion diaria",
      text: "Distribuir el consumo de agua durante el dia y revisar color de orina como guia practica.",
    },
    {
      id: "hidratacion_entreno",
      title: "Hidratacion en entrenamiento",
      text: toNumber(patient.entrenamientos_semana) > 0
        ? "En sesiones largas, intensas o con calor, planear bebida con electrolitos y carbohidratos segun duracion."
        : "Si se inicia entrenamiento, ajustar hidratacion segun duracion, calor e intensidad.",
    },
    {
      id: "limitar",
      title: "Alimentos a limitar",
      text: patient.objetivo_principal === "perdida_grasa"
        ? "Limitar bebidas azucaradas, fritos frecuentes, postres, embutidos y productos de alta densidad energetica."
        : "Mantener baja frecuencia de ultraprocesados y priorizar alimentos frescos.",
    },
    {
      id: "grasas_tecnicas",
      title: "Grasas y tecnicas culinarias",
      text: "Preferir aceite de oliva, aguacate y frutos secos en porciones medidas. Usar plancha, horno, vapor o guisos bajos en grasa.",
    },
    {
      id: "particulares",
      title: "Necesidades particulares",
      text: `${reportedRestrictions || "Sin restricciones reportadas"}. Revisar tolerancias, presupuesto y preferencias antes de entregar.`,
    },
  ];
}

export function generateRecommendations(patient, data = null) {
  const meals = activeMealTimes(patient);
  const objective = objectiveRules[patient.objetivo_principal]?.label ?? "objetivo individual";
  if (!data?.tables) return fallbackRecommendations(patient, meals, objective);

  const recs = [];
  const context = {
    objetivo: objective.toLowerCase(),
    objetivo_principal: patient.objetivo_principal ?? "",
    tiempos_comida: meals.length,
    tiempos_comida_lista: meals.map(mealLabel).join(", "),
    deporte: patient.deporte || "actividad reportada",
    presupuesto: patient.presupuesto || "no especificado",
  };
  const baseRows = sortedRecommendationRows(data, "recomendaciones_base")
    .filter((row) => appliesToPatient(row.aplica_a, patient));
  const intro = baseRows.find((row) => normalizeText(row.categoria).includes("introduccion")) || baseRows[0];

  if (intro) {
    pushRecommendation(recs, intro.id, intro.nombre, interpolateRecommendationText(intro.texto, context));
  } else {
    pushRecommendation(recs, "organizacion", "Organizacion del plan", fallbackRecommendations(patient, meals, objective)[0].text);
  }

  pushRecommendation(
    recs,
    "tiempos",
    "Tiempos de comida",
    `El plan usa ${meals.length} tiempos de comida (${meals.map(mealLabel).join(", ")}) para apoyar el objetivo de ${objective.toLowerCase()} y adaptarse a la rutina reportada.`
  );
  pushRecommendation(
    recs,
    "desayuno_raciones",
    "Desayuno y raciones",
    toNumber(patient.entrenamientos_semana) > 0
      ? "El desayuno integra una fuente de proteína, carbohidrato y grasa saludable. Las raciones pre y postentrenamiento se ajustan al horario, duración e intensidad reportadas."
      : "El desayuno integra una fuente de proteína, carbohidrato y grasa saludable. Si se activan raciones, deben ajustarse a hambre, horarios y objetivo."
  );

  const hydrationRows = sortedRecommendationRows(data, "recomendaciones_hidratacion", "prioridad")
    .filter((row) => appliesToPatient(row.condicion, patient));
  for (const row of hydrationRows) {
    pushRecommendation(recs, row.id, row.nombre, interpolateRecommendationText(row.texto, context));
  }

  const trainingRows = sortedRecommendationRows(data, "recomendaciones_entrenamiento", "prioridad")
    .filter((row) => toNumber(patient.entrenamientos_semana) > 0 && appliesToPatient(row.aplica_a, patient));
  for (const row of trainingRows) {
    const moment = row.momento ? `${row.momento}: ` : "";
    pushRecommendation(recs, row.id, row.nombre, `${moment}${interpolateRecommendationText(row.texto, context)}`);
  }

  const qualityRows = baseRows.filter((row) => row !== intro);
  const qualityText = qualityRows.map((row) => interpolateRecommendationText(row.texto, context)).join(" ");
  const restrictionText = formatRestrictionRecommendations(data, patient);
  pushRecommendation(
    recs,
    "limitar",
    "Alimentos a limitar y sustituciones",
    [qualityText, restrictionText].filter(Boolean).join(" ")
      || "Mantener baja frecuencia de ultraprocesados y priorizar alimentos frescos, porciones medidas y sustituciones acordes con tolerancias."
  );

  pushRecommendation(
    recs,
    "grasas_tecnicas",
    "Grasas y tecnicas culinarias",
    formatFatsAndTechniques(data) || fallbackRecommendations(patient, meals, objective).find((item) => item.id === "grasas_tecnicas").text
  );

  const clinicalText = formatClinicalConditionRecommendations(data, patient);
  pushRecommendation(
    recs,
    "particulares",
    "Necesidades particulares",
    [
      clinicalText,
      patient.observaciones_profesionales ? `Observaciones profesionales: ${patient.observaciones_profesionales}.` : "",
      `Presupuesto reportado: ${patient.presupuesto || "no especificado"}. Revisar tolerancias, preferencias y seguridad antes de entregar.`,
    ].filter(Boolean).join(" ")
  );

  return recs;
}

export const shoppingCategories = [
  "Proteínas",
  "Carbohidratos",
  "Frutas y verduras",
  "Grasas saludables",
  "Lácteos",
  "Suplementos/productos",
  "Otros",
];

const shoppingCatalog = [
  { category: "Proteínas", name: "Huevos", note: "Base para desayunos, cenas o raciones.", pattern: /\bhuevo(s)?\b/i },
  { category: "Proteínas", name: "Pollo", note: "Proteína principal para almuerzos, cenas o bowls.", pattern: /\bpollo\b/i },
  { category: "Proteínas", name: "Pescado", note: "Alternativa de proteína para comidas principales.", pattern: /\bpescado\b/i },
  { category: "Proteínas", name: "Atún", note: "Alternativa práctica de pescado; revisar sodio y presentación.", pattern: /\bat[uú]n\b/i },
  { category: "Proteínas", name: "Carne magra", note: "Proteína para comidas principales en porción definida.", pattern: /\bcarne magra\b/i },
  { category: "Proteínas", name: "Pavo", note: "Proteína magra para comidas principales.", pattern: /\bpavo\b/i },
  { category: "Proteínas", name: "Lentejas", note: "Proteína vegetal y fuente de fibra.", pattern: /\blenteja(s)?\b/i },
  { category: "Proteínas", name: "Garbanzos", note: "Proteína vegetal y fuente de fibra.", pattern: /\bgarbanzo(s)?\b/i },
  { category: "Proteínas", name: "Tofu", note: "Proteína vegetal para preparaciones principales.", pattern: /\btofu\b/i },
  { category: "Proteínas", name: "Hummus", note: "Preparación de garbanzo para refrigerios o acompañamientos.", pattern: /\bhummus\b/i },
  { category: "Carbohidratos", name: "Avena", note: "Para desayunos, preparaciones frías o raciones.", pattern: /\bavena\b/i },
  { category: "Carbohidratos", name: "Arroz integral", note: "Base de comidas principales.", pattern: /\barroz( integral)?\b/i },
  { category: "Carbohidratos", name: "Papa", note: "Tubérculo para comidas principales.", pattern: /\bpapa(s)?\b/i },
  { category: "Carbohidratos", name: "Arepa", note: "Opción práctica de desayuno o merienda.", pattern: /\barepa(s)?\b/i },
  { category: "Carbohidratos", name: "Tostadas de arroz", note: "Opción ligera para raciones o refrigerios.", pattern: /\btostada(s)? de arroz\b/i },
  { category: "Carbohidratos", name: "Cereal", note: "Para ración postentrenamiento según tolerancia.", pattern: /\bcereal\b/i },
  { category: "Frutas y verduras", name: "Banano", note: "Fruta práctica para desayuno o entrenamiento.", pattern: /\bbanano(s)?\b/i },
  { category: "Frutas y verduras", name: "Fruta variada", note: "Elegir opciones de temporada y preferencia.", pattern: /\bfruta(s)?\b/i },
  { category: "Frutas y verduras", name: "Vegetales", note: "Para ensaladas, bowls y acompañamientos.", pattern: /\bvegetal(es)?\b/i },
  { category: "Frutas y verduras", name: "Ensalada", note: "Comprar verduras frescas para ensaladas.", pattern: /\bensalada(s)?\b/i },
  { category: "Frutas y verduras", name: "Tomate", note: "Para desayunos, ensaladas y preparaciones saladas.", pattern: /\btomate(s)?\b/i },
  { category: "Grasas saludables", name: "Aguacate", note: "Grasa saludable para acompañar comidas.", pattern: /\baguacate(s)?\b/i },
  { category: "Grasas saludables", name: "Aceite de oliva", note: "Usar medido para cocción o aderezos.", pattern: /\baceite de oliva\b/i },
  { category: "Grasas saludables", name: "Frutos secos", note: "Porcionar para refrigerios.", pattern: /\bfrutos secos\b/i },
  { category: "Grasas saludables", name: "Semillas de chía", note: "Usar en porción medida en desayunos o refrigerios.", pattern: /\bsemilla(s)? de ch[ií]a\b|\bch[ií]a\b/i },
  { category: "Grasas saludables", name: "Mantequilla de maní", note: "Usar en porción medida y validar alergias.", pattern: /\bmantequilla de man[ií]\b/i },
  { category: "Lácteos", name: "Yogur griego", note: "Lácteo alto en proteína para refrigerios.", pattern: /\byogur(t)? griego\b|\byogur(t)?\b/i },
  { category: "Lácteos", name: "Queso bajo en grasa", note: "Opción láctea para meriendas.", pattern: /\bqueso bajo en grasa\b|\bqueso\b/i },
  { category: "Otros", name: "Bebida vegetal", note: "Elegir una opción compatible con alergias y fortificación requerida.", pattern: /\bbebida vegetal\b/i },
  { category: "Otros", name: "Compota sin azúcar", note: "Opción práctica solo cuando esté incluida en el plan.", pattern: /\bcompota\b/i },
  { category: "Otros", name: "Mermelada baja en azúcar", note: "Usar en porción pequeña si está incluida.", pattern: /\bmermelada baja en azucar\b|\bmermelada baja en azúcar\b/i },
  { category: "Otros", name: "Canela", note: "Condimento opcional para avena o yogurt.", pattern: /\bcanela\b/i },
];
function normalizeShoppingName(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function emptyShoppingGroups() {
  return shoppingCategories.map((category) => ({ category, items: [] }));
}

function addShoppingItem(groups, index, category, name, note, source) {
  const safeName = String(name ?? "").trim();
  if (!safeName) return;
  const safeCategory = shoppingCategories.includes(category) ? category : "Otros";
  const key = `${safeCategory}|${normalizeShoppingName(safeName)}`;
  const existing = index.get(key);
  if (existing) {
    if (source && !existing.source.split("; ").includes(source)) {
      existing.source = `${existing.source}; ${source}`;
    }
    if (note && !existing.note) existing.note = note;
    return;
  }
  const group = groups.find((item) => item.category === safeCategory);
  const item = {
    name: safeName,
    note: String(note ?? "").trim(),
    source: String(source ?? "Plan").trim(),
  };
  group.items.push(item);
  index.set(key, item);
}

function addCatalogMatches(groups, index, text, source) {
  for (const entry of shoppingCatalog) {
    if (entry.pattern.test(text)) {
      addShoppingItem(groups, index, entry.category, entry.name, entry.note, source);
    }
  }
}

function shoppingFoodIdentity(row) {
  const match = shoppingCatalog.find((entry) => entry.pattern.test(String(row.foodName ?? "")));
  if (match) return match;
  const category = {
    proteinas: "Proteínas",
    carbohidratos: "Carbohidratos",
    grasas: "Grasas saludables",
  }[row.groupId] ?? "Otros";
  return {
    category,
    name: String(row.foodName ?? "").trim(),
    note: "",
  };
}

function formatShoppingQuantity(grams) {
  const rounded = Math.round(grams);
  if (rounded >= 1000) {
    return `${Number((rounded / 1000).toFixed(2)).toLocaleString("es-CO")} kg`;
  }
  return `${rounded.toLocaleString("es-CO")} g`;
}

export function generateShoppingList(menu, selectedProductsOrOptions = [], maybeOptions = {}) {
  const options = Array.isArray(selectedProductsOrOptions)
    ? { products: selectedProductsOrOptions, ...maybeOptions }
    : selectedProductsOrOptions ?? {};
  const groups = emptyShoppingGroups();
  const index = new Map();
  const quantifiedFoods = new Map();

  for (const day of menu) {
    for (const meal of day.meals) {
      let hasStructuredFoods = false;
      for (const row of meal.calculatedPortions ?? []) {
        if (
          !(toNumber(row.grams) > 0)
          || !row.foodId
          || !row.foodName
          || !row.quantifiedFromFood
        ) continue;
        hasStructuredFoods = true;
        const identity = shoppingFoodIdentity(row);
        const key = `${identity.category}|${normalizeShoppingName(identity.name)}`;
        const current = quantifiedFoods.get(key) ?? {
          ...identity,
          grams: 0,
          meals: new Set(),
        };
        current.grams += toNumber(row.grams);
        current.meals.add(meal.label);
        quantifiedFoods.set(key, current);
      }
      if (meal.textEdited || !hasStructuredFoods) {
        const source = `Edición del día ${day.day} - ${meal.label}`;
        addCatalogMatches(groups, index, String(meal.text ?? ""), source);
      }
    }
  }

  for (const food of quantifiedFoods.values()) {
    const use = [...food.meals].join(", ");
    addShoppingItem(
      groups,
      index,
      food.category,
      food.name,
      `Cantidad semanal calculada: ${formatShoppingQuantity(food.grams)} aprox.${use ? ` Uso: ${use}.` : ""}${food.note ? ` ${food.note}` : ""}`,
      "Menú cuantificado de 7 días",
    );
  }

  for (const recipe of options.recipes ?? []) {
    const source = `Receta - ${recipe.nombre}`;
    addCatalogMatches(groups, index, String(recipe.ingredientes ?? ""), source);
  }

  for (const product of options.products ?? []) {
    const note = product.motivo_recomendacion || product.descripcion || product.donde_se_consigue || "Producto recomendado.";
    const source = product.donde_se_consigue ? `Producto recomendado - ${product.donde_se_consigue}` : "Producto recomendado";
    addShoppingItem(groups, index, "Suplementos/productos", product.nombre, note, source);
  }

  for (const supplement of options.supplements ?? []) {
    const note = supplement.indicacion_consumo || supplement.observaciones || supplement.descripcion || "Suplemento opcional.";
    addShoppingItem(groups, index, "Suplementos/productos", supplement.nombre, note, "Suplemento seleccionado");
  }

  for (const group of groups) {
    group.items.sort((a, b) => a.name.localeCompare(b.name, "es"));
  }

  return groups.filter((group) => group.items.length > 0);
}
